<!-- Ported from the Polaris kit: src/product/components/EditSheet.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase P, 28/09):
     - Frame: DrawerFrame.vue (PROVISÓRIO, R2-X-3) instead of the registry Sheet: a column from 1280px, the
       non-modal overlay below. SheetHeader/SheetFooter stay (plain divs); SheetTitle/SheetDescription need the
       Sheet's dialog context, so they are an h2 and a p with the same classes.
     - Width: the kit's `--drawer-width` (43.875rem, src/styles/tokens/components.css) is not synced to this app;
       written as a literal. In the column it is capped at 60% of the row.
     - The header leaves room for the close button (`pe-12`, logical so RTL mirrors it), which the Sheet put over it.
     - The guard lives in useDiscardGuard.js + DiscardChangesAlert.vue; `requestClose` and `runGuarded` are exposed.
     - Added: `mode` (DrawerFrame), `saveLabel`, `canSave` (Save disabled while false: invalid or saving). -->
<script setup>
// Editing has a beginning and an end (principle P-07): one panel, a scope line that says what is being edited,
// Save or Cancel, and no way to lose edits silently. No styles of its own.
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from '../ui/button';
import { SheetFooter, SheetHeader } from '../ui/sheet';
import DrawerFrame from './DrawerFrame.vue';
import DiscardChangesAlert from './DiscardChangesAlert.vue';
import { useDiscardGuard } from './useDiscardGuard';

const props = defineProps({
  open: Boolean,
  title: { type: String, required: true },
  scope: { type: String, required: true }, // "Settings · SLA"
  dirty: Boolean,
  saveLabel: { type: String, default: '' },
  canSave: { type: Boolean, default: true },
  mode: { type: String, default: 'auto' },
});
const emit = defineEmits(['update:open', 'save']);
const { t } = useI18n();

const keepId = `edit-sheet-keep-${useId()}`;
const { confirming, requestClose, runGuarded, keepEditing, discard } =
  useDiscardGuard({
    isOpen: () => props.open,
    isDirty: () => props.dirty,
    close: () => emit('update:open', false),
    keepId,
  });
defineExpose({ requestClose, runGuarded });
const label = computed(() => `${props.scope}: ${props.title}`);
</script>

<template>
  <DrawerFrame
    :open="open"
    :label="label"
    :mode="mode"
    column-class="w-[min(43.875rem,60%)]"
    overlay-class="sm:max-w-[43.875rem]"
    data-edit-sheet
    @close="requestClose"
  >
    <SheetHeader class="border-b border-solid border-polaris-border pe-12">
      <p
        class="type-polaris-caption font-medium uppercase tracking-[0.06em] text-polaris-muted-foreground"
      >
        {{ scope }}
      </p>
      <h2 class="type-polaris-section-title text-polaris-foreground">
        {{ title }}
      </h2>
    </SheetHeader>
    <div class="flex-1 overflow-auto p-4">
      <DiscardChangesAlert
        v-if="confirming"
        :keep-id="keepId"
        @keep="keepEditing"
        @discard="discard"
      />
      <div v-show="!confirming" class="grid gap-4"><slot /></div>
    </div>
    <SheetFooter
      v-if="!confirming"
      class="flex-row justify-end border-t border-solid border-polaris-border"
    >
      <Button type="button" variant="outline" @click="requestClose">
        {{ t('POLARIS.CONFIRM_BUTTON.CANCEL') }}
      </Button>
      <Button type="button" :disabled="!canSave" @click="emit('save')">
        {{ saveLabel || t('POLARIS.DRAWER.SAVE_CHANGES') }}
      </Button>
    </SheetFooter>
  </DrawerFrame>
</template>
